import {
  Button,
  Checkbox,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useToast
} from '@chakra-ui/react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import Bowser from 'bowser'
import { useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

import { termInstance } from '@/modules/terms'
import { Term } from '@/modules/terms/domain/models/Term'
import { useAuthContext } from '@/ui/contexts/AuthContext'

export default function Terms({ data }: { data: Term[] }) {
  const queryClient = useQueryClient()
  const toast = useToast()
  const { t } = useTranslation()

  const { userSession } = useAuthContext()
  const userId = userSession?.userId
  const contentRef = useRef<HTMLDivElement>(null)

  const [isTermsAccepted, setIsTermsAccepted] = useState(false)
  const [isAllTermsAccepted, setIsAllTermsAccepted] = useState(false)
  const [termIndex, setTermIndex] = useState(0)

  const updateTermMutation = useMutation({
    mutationKey: ['terms', 'accept', userId, window.navigator.userAgent],
    mutationFn: async () =>
      userId &&
      (await termInstance.userAcceptTerm(
        userId!.toString(),
        JSON.stringify(Bowser.parse(window.navigator.userAgent))
      )),
    onError() {
      toast({
        status: 'error',
        description: t('auth.term.status.unknownError')
      })
    },
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: ['terms'] })
      toast({
        status: 'success',
        description: t('auth.term.status.termsAccepted')
      })
    }
  })

  return (
    <Modal
      isOpen
      closeOnEsc={false}
      closeOnOverlayClick={false}
      scrollBehavior={'inside'}
      size={'xl'}
      isCentered
      onClose={() => {}}
    >
      <ModalOverlay />
      <form
        onSubmit={(e) => {
          e.preventDefault()
          if (isAllTermsAccepted) return updateTermMutation.mutate()
          contentRef.current?.scrollTo({
            behavior: 'instant',
            top: 0
          })
          setTermIndex((prev) => ++prev)
          setIsTermsAccepted(false)
        }}
      >
        <ModalContent>
          <ModalHeader>
            <Text>{t('auth.term.modal.title')}</Text>
          </ModalHeader>
          <ModalBody ref={contentRef}>
            <Flex>
              {data.length < 1 ? (
                <>{t('general.common.empty.message')}</>
              ) : (
                <Flex key={data[termIndex].id} flexDir={'column'}>
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {data[termIndex].description}
                  </ReactMarkdown>
                  <Flex alignItems={'center'} mt={'4'}>
                    <Checkbox
                      size={'lg'}
                      mr={'4'}
                      onChange={(e) => {
                        setIsTermsAccepted(e.currentTarget.checked)
                        if (data.length - 1 === termIndex)
                          setIsAllTermsAccepted(e.currentTarget.checked)
                      }}
                    />
                    <Text>
                      <Trans i18nKey="auth.term.modal.acceptTerm" />
                    </Text>
                  </Flex>
                </Flex>
              )}
            </Flex>
          </ModalBody>
          <ModalFooter>
            <Flex flexDir={'column'} w={'full'} rowGap={'4'}>
              <Button
                colorScheme={'blue'}
                isDisabled={!isTermsAccepted || updateTermMutation.isPending}
                type={'submit'}
              >
                {t('auth.term.modal.continue')}
              </Button>
            </Flex>
          </ModalFooter>
        </ModalContent>
      </form>
    </Modal>
  )
}
